import { fromJS } from 'immutable';
import { makeImmutable } from 'utils/immutableUtils';
import ConditionalityGraph from 'utils/conditionality.graph';
import { DIRECTIONS, INFERRABLE_FIELD_NAME_MAP } from 'utils/constants';
import {
  createDefaultFieldStateFromFieldData,
  createDefaultInvalidOverrideDataForType,
  createDefaultOverrideStateForType,
  createDefaultOverrideUIStateForType,
} from 'utils/editorDefaultStates';
import { ERROR_TEXT_EMPTY_REQUIRED_FIELD } from 'langs/en/errors';
import { trimEmptyLeaves, makeDirty } from './ops.ui';
import { updateStepValidity } from './ops.step';

const initialFieldInvalidState = {
  name: {
    error: {
      visible: false,
      text: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
    },
  },
  label: {
    error: {
      visible: false,
      text: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
    },
  },
};

export function addField(state, id, index) {
  const graph = new ConditionalityGraph(state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']).toJS());
  const fieldVertex = graph.findOrCreateVertex({ left: id, action: 'render if' });
  graph.addPath([0, fieldVertex], fieldVertex);
  const newFieldValue = makeImmutable(createDefaultFieldStateFromFieldData(id));
  const newFieldUIState = makeImmutable({
    label: {
      isValid: false,
      required: true,
      errorDisplay: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
    },
    name: {
      isValid: false,
      required: true,
      errorDisplay: ERROR_TEXT_EMPTY_REQUIRED_FIELD,
    },
    type: {
      isValid: true,
      required: true,
    },
    editors: {},
    helpText: { hasHelpText: {}, value: {} },
    overrides: {
      gdpr: {
        label: {},
        helpText: { value: {} },
        required: {},
        hidden: {},
      },
    },
  });

  return makeDirty(
    updateStepValidity(
      state.setIn(['form', 'fields', id], newFieldValue)
        .setIn(['meta', 'uiState', 'fields', id], newFieldUIState)
        .setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate()))
        .setIn(['meta', 'invalidFormData', 'fields', id], fromJS(initialFieldInvalidState))
        .updateIn(['form', 'fieldOrder'], (arr) => (index) ? arr.splice(index, 0, id) : arr.push(id))
        .updateIn(['meta', 'uiState', 'fieldOrderCollapsedState'], (arr) => (index) ? arr.splice(index, 0, false) : arr.push(false))
    )
  );
}

export function deleteField(state, keyPath) {
  const fieldId = keyPath[keyPath.length - 1];
  const fieldOrderIndex = state.getIn(['form', 'fieldOrder']).toJS().indexOf(fieldId);
  const graph = new ConditionalityGraph(state.getIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph']).toJS());
  const fieldName = state.getIn(['form', 'fields', fieldId, 'name']);

  let newState = state;
  if (newState.getIn(['form', 'syntheticContentEntities', 'inferredFields', fieldName])) {
    const otherFieldsWithName = newState.getIn(['form', 'fields']).count((field) => field.get('id') !== fieldId && field.get('name') === fieldName && !field.get('delete'));
    if (!otherFieldsWithName) {
      newState = newState.deleteIn(['form', 'syntheticContentEntities', 'inferredFields', fieldName]);
    }
  }
  graph.orphanFieldVertex(graph.findOrCreateVertex({ left: fieldId }));
  return trimEmptyLeaves(
    makeDirty(
      updateStepValidity(
        newState.setIn(['form', ...keyPath, 'delete'], true)
          .setIn(['form', 'fieldConditionalityInfo', 'dehydratedGraph'], fromJS(graph.dehydrate()))
          .updateIn(['form', 'fieldOrder'], (arr) => arr.filter((id) => id !== fieldId))
          .updateIn(['meta', 'uiState', 'fieldOrderCollapsedState'], (arr) => arr.splice(fieldOrderIndex, 1))
          .deleteIn(['meta', 'uiState', ...keyPath])
      )
    ), ['meta', 'invalidFormData', ...keyPath]);
}

export function moveField(state, source, target) {
  // make the fields swappable if they're right next to each other
  const { from, to } = ((source + 1) === target) ? { to: target, from: source } : { to: target, from: source };
  const js = state.toJS();
  const field = js.form.fieldOrder[from];
  let collapsedState = js.meta.uiState.fieldOrderCollapsedState;

  if (from > to) {
    js.form.fieldOrder = [...js.form.fieldOrder.slice(0, to), field, ...js.form.fieldOrder.slice(to, from), ...js.form.fieldOrder.slice(from + 1)];
    collapsedState = [...collapsedState.slice(0, to), collapsedState[from], ...collapsedState.slice(to, from), ...collapsedState.slice(from + 1)];
  } else if (from < to) {
    js.form.fieldOrder = [...js.form.fieldOrder.slice(0, from), ...js.form.fieldOrder.slice(from + 1, to), field, ...js.form.fieldOrder.slice(to)];
    collapsedState = [...collapsedState.slice(0, from), ...collapsedState.slice(from + 1, to), collapsedState[from], ...collapsedState.slice(to)];
  }
  js.meta.uiState.fieldOrderCollapsedState = collapsedState;

  return makeDirty(updateStepValidity(fromJS(js)));
}

export function reorderDropdown(state, action) {
  const { keyPath, optionId, direction } = action;
  const optionsOrder = state.getIn(['form', ...keyPath]).toJS();
  const index = optionsOrder.findIndex((el) => el === optionId);

  if (direction === DIRECTIONS.UP) {
    if (index === 0) return state;
    const temp = optionsOrder[index - 1];
    optionsOrder[index - 1] = optionsOrder[index];
    optionsOrder[index] = temp;
  } else if (direction === DIRECTIONS.DOWN) {
    if (index === state.getIn(['form', ...keyPath]).size - 1) return state;
    const temp = optionsOrder[index + 1];
    optionsOrder[index + 1] = optionsOrder[index];
    optionsOrder[index] = temp;
  } else {
    return state;
  }
  const newState = state.setIn(['form', ...keyPath], fromJS(optionsOrder));
  return makeDirty(newState);
}

export function updateFieldOverrideState(state, fieldId, fieldType, consentStyle) {
  let newState = state;
  const overrideKeypath = ['form', 'fields', fieldId, 'overrides'];
  const overrideId = newState.getIn([...overrideKeypath, 'gdpr', 'id']);

  const fieldName = state.getIn(['form', 'fields', fieldId, 'name']);
  const defaultOverrideState = createDefaultOverrideStateForType(fieldType, consentStyle, fieldName);

  const hasIncomingOverride = defaultOverrideState && Object.keys(defaultOverrideState).length;
  if (hasIncomingOverride) {
    newState = newState.removeIn(['form', 'fields', fieldId, 'overrides', 'gdpr', 'delete']);
    newState = newState.setIn(overrideKeypath, fromJS(defaultOverrideState));
    if (overrideId) {
      newState = newState.setIn([...overrideKeypath, 'gdpr', 'id'], overrideId);
    }
  } else {
    if (overrideId) { // eslint-disable-line no-lonely-if
      const smsOverridePaths = [
        [...overrideKeypath, 'gdpr', 'editors', 'defaultOption'],
        [...overrideKeypath, 'gdpr', 'value', 'defaultOption'],
      ];

      if (smsOverridePaths.some((path) => newState.hasIn(path))) {
        // Remove all SMS default option override payloads and retain other gdpr fields
        smsOverridePaths.forEach((path) => {
          if (newState.hasIn(path)) {
            newState = newState.removeIn(path);
          }
        });
        newState = trimEmptyLeaves(newState, [...overrideKeypath, 'gdpr', 'editors']);
        newState = trimEmptyLeaves(newState, [...overrideKeypath, 'gdpr', 'value']);
      }

      newState = newState.setIn([...overrideKeypath, 'gdpr', 'delete'], true);
    } else {
      newState = newState.removeIn(overrideKeypath);
    }
  }

  const uiOverrides = createDefaultOverrideUIStateForType(fieldType, consentStyle);
  newState = newState.setIn(['meta', 'uiState', 'fields', fieldId, 'overrides'], fromJS(uiOverrides));

  const overrideErrors = createDefaultInvalidOverrideDataForType(fieldType, consentStyle);
  if (overrideErrors && Object.keys(overrideErrors).length) {
    newState = newState.setIn(['meta', 'invalidFormData', 'fields', fieldId, 'overrides'], fromJS(overrideErrors));
  } else {
    newState = trimEmptyLeaves(newState, ['meta', 'invalidFormData', 'fields', fieldId, 'overrides', 'gdpr']);
  }

  return newState;
}

export function updateFormValidity(state, keyPath, isValid, errorDisplay, replaceError) {
  let newState = state;
  if (!isValid) {
    if (replaceError || !newState.hasIn(['meta', 'invalidFormData', ...keyPath, 'error'])) {
      newState = newState.mergeIn(['meta', 'uiState', ...keyPath], { isValid: false, errorDisplay, className: 'is-invalid' });
      newState = newState.setIn(['meta', 'invalidFormData', ...keyPath, 'error'], fromJS({ visible: false, text: errorDisplay }));
    }
  } else if (newState.hasIn(['meta', 'invalidFormData', ...keyPath, 'error'])) {
    newState = state.mergeIn(['meta', 'uiState', ...keyPath], { isValid: true, errorDisplay: '', className: '' });
    newState = trimEmptyLeaves(newState, ['meta', 'invalidFormData', ...keyPath]);
  }
  return newState;
}

export function collapseAllFields(state) {
  const fieldOrderSize = state.getIn(['meta', 'uiState', 'fieldOrderCollapsedState']).size;
  return state.setIn(['meta', 'uiState', 'fieldOrderCollapsedState'], fromJS(Array(fieldOrderSize).fill(true)));
}

export function expandAllFields(state) {
  const fieldOrderSize = state.getIn(['meta', 'uiState', 'fieldOrderCollapsedState']).size;
  return state.setIn(['meta', 'uiState', 'fieldOrderCollapsedState'], fromJS(Array(fieldOrderSize).fill(false)));
}

export function bulkToggleDropdownOptions(state, fieldId, dropdownOptions, value) {
  let newState = state;
  dropdownOptions.forEach((key) => {
    newState = newState.setIn(['form', 'fields', fieldId, 'editors', 'dropdownConfig', 'options', key, 'shouldDisplay'], value);
  });
  return newState;
}

export function setInferredState(state, fieldName, isInferredChecked, fieldId) {
  const pathToFormFields = ['form', 'fields'];
  const pathToInferredFieldName = ['form', 'syntheticContentEntities', 'inferredFields', fieldName];

  let newState = updateFormValidity(state, ['fields', fieldId, 'editors', 'value'], true);
  newState = isInferredChecked ?
    newState.setIn(pathToInferredFieldName, true) :
    newState.deleteIn(pathToInferredFieldName);

  if (INFERRABLE_FIELD_NAME_MAP[fieldName].shouldHideField) {
    const stateForFields = state.getIn(pathToFormFields);
    const updatedHiddenRequired = state.getIn([...pathToFormFields, fieldId])
      .set('hidden', isInferredChecked)
      .set('required', !isInferredChecked);
    const updatedFields = stateForFields.setIn([fieldId], updatedHiddenRequired);
    return newState.setIn(pathToFormFields, updatedFields);
  }

  return newState;
}
